// #452 sold-out availability: the SINGLE key model + cascade resolver, keyed
// on the SAME SKU identity the catalog / cart / checkout already use (variety
// slug + color-choice + length). Pure + dependency-free so every surface
// (catalog, cart reconciler, repricing, order gate, admin, compras) resolves
// "is this line sold out?" identically and so the cascade is unit-testable
// without DynamoDB.
//
// Three scope granularities, encoded as flat string keys:
//   V#<slug>                     whole variety   (cascades to all colors+lengths)
//   C#<slug>#<color>             one color       (cascades to that color's lengths)
//   L#<slug>#<color|->#<length>  one length      (the rare drill; color "-" = any)
//
// Default = available: a scope is sold out ONLY when its key is present in the
// sold-out set. Restock = remove the key.

export type AvailabilityScope = "variety" | "color" | "length";

// A cart/catalog line's identity as the resolver sees it. Mirrors the
// slug + colorChoice + length tuple used by lib/cart/sync.ts lineKey.
export type SkuQuery = {
  slug: string;
  colorChoice?: string | null;
  length?: number | null;
};

const COLOR_ANY = "-";

function normColor(color: string | null | undefined): string {
  const c = (color ?? "").trim();
  return c.length > 0 ? c : COLOR_ANY;
}

export function varietyKey(slug: string): string {
  return `V#${slug}`;
}

export function colorKey(slug: string, color: string): string {
  return `C#${slug}#${normColor(color)}`;
}

// color "-" (or null) = a variety-wide length toggle that applies regardless of
// the buyer's color choice; a concrete color = a per-color length toggle.
export function lengthKey(
  slug: string,
  color: string | null | undefined,
  length: number,
): string {
  return `L#${slug}#${normColor(color)}#${length}`;
}

// Build the key for an arbitrary scope, used by the toggle write path.
export function buildKey(input: {
  scope: AvailabilityScope;
  slug: string;
  color?: string | null;
  length?: number | null;
}): string {
  if (input.scope === "variety") return varietyKey(input.slug);
  if (input.scope === "color") return colorKey(input.slug, input.color ?? "");
  if (typeof input.length !== "number") {
    throw new Error("length scope requires a numeric length");
  }
  return lengthKey(input.slug, input.color, input.length);
}

// THE cascade. A line { slug, colorChoice, length } is sold out when ANY
// broader-or-equal scope key covering it is present:
//   - the whole variety is sold out, OR
//   - the chosen color is sold out, OR
//   - the chosen length is sold out (variety-wide "-" form, or the exact
//     color+length form).
// A variety-level key therefore covers every color and length by construction;
// a color-level key covers that color's lengths. Purely a set-membership test.
export function isSoldOut(
  soldOutKeys: ReadonlySet<string>,
  query: SkuQuery,
): boolean {
  if (soldOutKeys.size === 0) return false;
  const { slug } = query;
  if (soldOutKeys.has(varietyKey(slug))) return true;

  const color = query.colorChoice ?? null;
  if (color && soldOutKeys.has(colorKey(slug, color))) return true;

  if (typeof query.length === "number") {
    if (soldOutKeys.has(lengthKey(slug, null, query.length))) return true;
    if (color && soldOutKeys.has(lengthKey(slug, color, query.length))) {
      return true;
    }
  }
  return false;
}
